/**
 * A rule's law, on request — the ⓘ beside its name. What the row leaves out
 * to stay clear is all here, nothing lost: the window it counts in, what can
 * follow past its limit and the article that says so, and the exceptions the
 * law names (shown, never applied). Why a rule has no number yet, and whose
 * it is to settle, and the band where two norms disagree, are on the row
 * itself (AC-2).
 */
import { INOUT_CONSEQUENCE_KEY, INOUT_EXCEPTION_TIMING_KEY, type InOutClock } from '@tonnie/heroes';
import { useLocale } from '@tonnie/locale';
import type { InOutExceptionInfo } from '@tonnie/types';
import { InfoPopover } from '@tonnie/ui';

interface InOutRuleInfoProps {
  clock: InOutClock;
  /** The rule's name, as the row says it. */
  name: string;
  exceptions: InOutExceptionInfo[];
}

export function InOutRuleInfo({ clock, name, exceptions }: InOutRuleInfoProps) {
  const { t, formatDate } = useLocale();
  const named = exceptions.filter((e) => clock.exceptions.includes(e.id));

  return (
    <InfoPopover label={t('inout.clock.info', { rule: name })} data-testid="inout-clock-info">
      <p className="font-semibold text-foreground">{name}</p>
      {clock.window && (
        <p className="text-muted-foreground">
          {clock.window.to
            ? t('inout.clock.window', {
                from: formatDate(clock.window.from, 'calendarDay'),
                to: formatDate(clock.window.to, 'calendarDay'),
              })
            : t('inout.clock.windowOpen', {
                from: formatDate(clock.window.from, 'calendarDay'),
              })}
        </p>
      )}
      <div className="space-y-0.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('inout.clock.lawTitle')}
        </p>
        <p className="text-foreground">{t(INOUT_CONSEQUENCE_KEY[clock.consequence])}</p>
        <p className="text-xs text-muted-foreground">{clock.source}</p>
      </div>
      {named.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('inout.clock.exceptionsTitle')}
          </p>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {named.map((e) => (
              <li key={e.id}>
                <span className="font-medium text-foreground">{e.label}</span>
                <span className="block">
                  {t('inout.clock.exceptionTiming', {
                    timing: t(INOUT_EXCEPTION_TIMING_KEY[e.timing]),
                    source: e.source,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </InfoPopover>
  );
}
